import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SUBSCRIPTION_STATUSES } from '@subs/shared';

import {
  subscriptions,
  vendorGroups,
  type Subscription,
} from '../api/client';
import { CredentialsDialog } from '../components/CredentialsDialog';
import { SubscriptionFormModal } from '../components/SubscriptionFormModal';
import { useToast } from '../components/Toast';
import {
  Button,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  Select,
  Spinner,
  StatusPill,
  Input,
} from '../components/ui';
import { clipList, formatAmount, formatCountdown, formatRenewal } from '../lib/format';
import { toInt, useQueryParams } from '../lib/query-params';
import { useSession } from '../lib/use-session';

const EXPIRING_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '7', label: 'Next 7 days' },
  { value: '14', label: 'Next 14 days' },
  { value: '30', label: 'Next 30 days' },
  { value: '90', label: 'Next 90 days' },
] as const;

const PAGE_SIZE = 25;

/** Toolbar + table + row actions for `/subscriptions`. */
export function SubscriptionsPage() {
  const { params, setParams } = useQueryParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [editing, setEditing] = useState<Subscription | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [credentialsFor, setCredentialsFor] = useState<Subscription | null>(null);
  const [blocking, setBlocking] = useState<Subscription | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [finishing, setFinishing] = useState<Subscription | null>(null);

  const q = params.q ?? '';
  const status = (params.status ?? '') as '' | (typeof SUBSCRIPTION_STATUSES)[number];
  const groupId = params.groupId ?? '';
  const expiringWithinDays = toInt(params.expiringWithinDays);
  const page = toInt(params.page) ?? 1;

  const filters = {
    q: q || undefined,
    status: status || undefined,
    groupId: groupId || undefined,
    expiringWithinDays,
    page,
    limit: PAGE_SIZE,
  };

  const list = useQuery({
    queryKey: ['subscriptions', filters],
    queryFn: ({ signal }) => subscriptions.list(filters, signal),
    placeholderData: keepPreviousData,
  });

  const groups = useQuery({
    queryKey: ['vendor-groups', 200],
    queryFn: ({ signal }) => vendorGroups.list({ limit: 200 }, signal),
    staleTime: 60_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    queryClient.invalidateQueries({ queryKey: ['payments'] });
  };

  const block = useMutation({
    mutationFn: (input: { id: string; reason: string }) => subscriptions.block(input.id, input.reason),
    onSuccess: (updated) => {
      invalidate();
      toast(`Blocked ${updated.name}`, 'success');
      setBlocking(null);
      setBlockReason('');
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const unblock = useMutation({
    mutationFn: (id: string) => subscriptions.unblock(id),
    onSuccess: (updated) => {
      invalidate();
      toast(`Unblocked ${updated.name}`, 'success');
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const finish = useMutation({
    mutationFn: (input: { id: string; status: 'FINISHED' }) =>
      subscriptions.update(input.id, { status: input.status }),
    onSuccess: (updated) => {
      invalidate();
      toast(`${updated.name} marked FINISHED`, 'success');
      setFinishing(null);
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  // VIEW users get the read-only surface: the mutating controls below are not
  // merely disabled but absent (TASKS.md B7) — the api would 403 them anyway.
  const { canEdit } = useSession();

  const items = list.data?.items ?? [];
  const meta = list.data?.meta;
  const totalPages = meta?.totalPages ?? 1;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Subscriptions</h1>
          <p className="page-header__sub">
            {meta ? `${meta.total} subscription${meta.total === 1 ? '' : 's'}` : 'Corporate subscriptions, renewals and assignment'}
          </p>
        </div>
        <div className="page-header__actions">
          {canEdit ? (
            <Button
              variant="primary"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              New subscription
            </Button>
          ) : null}
        </div>
      </div>

      <div className="card card--flush">
        <div className="toolbar" style={{ padding: 'var(--space-4)', marginBottom: 0 }}>
          <div className="toolbar__field" style={{ minWidth: 220 }}>
            <label className="toolbar__label" htmlFor="filter-q">Search</label>
            <Input
              id="filter-q"
              type="search"
              placeholder="Name or purpose…"
              value={q}
              onChange={(event) => setParams({ q: event.target.value, page: undefined })}
            />
          </div>
          <div className="toolbar__field">
            <label className="toolbar__label" htmlFor="filter-status">Status</label>
            <Select
              id="filter-status"
              value={status}
              onChange={(event) => setParams({ status: event.target.value || undefined, page: undefined })}
            >
              <option value="">Any status</option>
              {SUBSCRIPTION_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </div>
          <div className="toolbar__field">
            <label className="toolbar__label" htmlFor="filter-group">Group</label>
            <Select
              id="filter-group"
              value={groupId}
              onChange={(event) => setParams({ groupId: event.target.value || undefined, page: undefined })}
            >
              <option value="">Any group</option>
              {(groups.data?.items ?? []).map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="toolbar__field">
            <label className="toolbar__label" htmlFor="filter-expiring">Expiring</label>
            <Select
              id="filter-expiring"
              value={expiringWithinDays ? String(expiringWithinDays) : ''}
              onChange={(event) =>
                setParams({ expiringWithinDays: event.target.value || undefined, page: undefined })
              }
            >
              {EXPIRING_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="toolbar__spacer" />
          {q || status || groupId || expiringWithinDays ? (
            <Button variant="ghost" size="sm" onClick={() => setParams({ q: undefined, status: undefined, groupId: undefined, expiringWithinDays: undefined, page: undefined })}>
              Reset filters
            </Button>
          ) : null}
        </div>

        {list.isPending ? (
          <Spinner label="Loading subscriptions" />
        ) : list.isError ? (
          <ErrorState message={list.error.message} onRetry={() => void list.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            title="No subscriptions match"
            hint="Loosen the filters, or add the first subscription."
            action={
              canEdit ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setEditing(null);
                    setFormOpen(true);
                  }}
                >
                  New subscription
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th scope="col">Subscription</th>
                    <th scope="col">Group</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="num">Cost / renewal</th>
                    <th scope="col" className="num">Expires</th>
                    <th scope="col">Assigned</th>
                    <th scope="col">
                      <span className="visually-hidden" style={{ position: 'absolute', left: -9999 }}>Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const countdown = formatCountdown(item.expiresAt);
                    const rowTone =
                      item.effectiveStatus === 'EXPIRED'
                        ? 'row--expired'
                        : item.effectiveStatus === 'BLOCKED'
                          ? 'row--blocked'
                          : '';
                    const assigned = clipList(item.assignedUsers, 2);
                    return (
                      <tr key={item.id} className={rowTone}>
                        <td>
                          {item.url ? (
                            <a className="cell-primary" href={item.url} target="_blank" rel="noreferrer noopener">
                              {item.name}
                            </a>
                          ) : (
                            <span className="cell-primary">{item.name}</span>
                          )}
                          {item.purpose ? (
                            <span className="cell-sub" title={item.purpose}>
                              {item.purpose}
                            </span>
                          ) : null}
                        </td>
                        <td>
                          {item.group ? (
                            <span className="badge-cell" title={item.group.name}>
                              <span className="badge badge--brand">{item.group.name}</span>
                            </span>
                          ) : (
                            <span className="cell-muted">—</span>
                          )}
                        </td>
                        <td>
                          <StatusPill status={item.effectiveStatus} reason={item.blockedReason} />
                        </td>
                        <td className="num">
                          {formatRenewal(item.renewalCost, item.currency, item.renewalPeriod)}
                          <span className="matrix__rub">initial {formatAmount(item.initialCost, item.currency)}</span>
                        </td>
                        <td className="num">
                          {countdown.text}
                          <span className={`matrix__rub${countdown.tone === 'soon' || countdown.tone === 'past' ? ' matrix__rub--warn' : ''}`}>
                            {new Date(item.expiresAt).toISOString().slice(0, 10)}
                          </span>
                        </td>
                        <td>
                          {assigned.visible.length === 0 ? (
                            <span className="cell-muted">—</span>
                          ) : (
                            <span>
                              {assigned.visible.map((email) => (
                                <span className="email-chip" key={email} title={email}>
                                  {email}
                                </span>
                              ))}
                              {assigned.extra > 0 ? (
                                <span className="email-chip" title={item.assignedUsers.join(', ')}>
                                  +{assigned.extra}
                                </span>
                              ) : null}
                            </span>
                          )}
                        </td>
                        <td>{canEdit ? (
                          <div className="row-actions">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                setEditing(item);
                                setFormOpen(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button size="sm" variant="secondary" onClick={() => setCredentialsFor(item)}>
                              Credentials
                            </Button>
                            {item.status === 'BLOCKED' ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                loading={unblock.isPending && unblock.variables === item.id}
                                onClick={() => unblock.mutate(item.id)}
                              >
                                Unblock
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  setBlocking(item);
                                  setBlockReason(item.blockedReason ?? '');
                                }}
                              >
                                Block
                              </Button>
                            )}
                            {item.status === 'FINISHED' ? null : (
                              <Button size="sm" variant="secondary" onClick={() => setFinishing(item)}>
                                Finish
                              </Button>
                            )}
                          </div>
                        ) : null}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <span>
                Page {meta?.page ?? page} of {totalPages}
                {meta ? ` · ${meta.total} total` : ''}
              </span>
              <div className="pagination__pages">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setParams({ page: page - 1 })}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= totalPages}
                  onClick={() => setParams({ page: page + 1 })}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <SubscriptionFormModal
        open={formOpen}
        subscription={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />

      <CredentialsDialog
        open={Boolean(credentialsFor)}
        subscription={credentialsFor}
        onClose={() => setCredentialsFor(null)}
      />

      <Modal
        open={Boolean(blocking)}
        onClose={() => setBlocking(null)}
        title="Block subscription"
        description="It stays in the history with all of its payments — only the reason is recorded."
        footer={
          <>
            <Button variant="secondary" onClick={() => setBlocking(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={block.isPending}
              disabled={!blockReason.trim()}
              onClick={() => blocking && block.mutate({ id: blocking.id, reason: blockReason })}
            >
              Block
            </Button>
          </>
        }
      >
        <Field label="Reason" htmlFor="block-reason" required hint="Shown on the gray BLOCKED pill">
          <Input
            id="block-reason"
            value={blockReason}
            onChange={(event) => setBlockReason(event.target.value)}
            placeholder="Account blocked by the vendor, moved to a new one"
          />
        </Field>
      </Modal>

      <Modal
        open={Boolean(finishing)}
        onClose={() => setFinishing(null)}
        title="Finish subscription"
        description="Marks it FINISHED — the payments stay, renewals stop being tracked."
        footer={
          <>
            <Button variant="secondary" onClick={() => setFinishing(null)}>
              Cancel
            </Button>
            <Button variant="primary" loading={finish.isPending} onClick={() => finishing && finish.mutate({ id: finishing.id, status: 'FINISHED' })}>
              Mark FINISHED
            </Button>
          </>
        }
      >
        <p>Finish “{finishing?.name}”?</p>
      </Modal>
    </>
  );
}
